import { View } from "../../../../../core/view/index";
import { Template } from "../../../../../core/template/index";
import BBPlugin from "../../../../../core/bbclient/plugin";
import AppIcons from "../../../../icons";
import { ViewDataBindEvents, DataPropertyChangedEventArgs } from "../../../../../core/view/data-bind/index";
import MessageModal from "../../../../../core/modals/message/index";
import { ModalColor } from "../../../../../core/modal/index";
import BBrowser from "../../../../../core/bbclient/browser";
import { SelectOptionData } from "../../../../../core/view/data-bind/types/selectbox-base";
import { FortumApp } from "../../../../fortumApp";
import KeyCode from "../../../../../core/helpers/keycode";
import { Order } from "../../../../db/models/decontamination/order";

const KEY_REMEMBER_PREFERRED_CAMERA = 'preferredCamera';

export default class ScanOrderView extends View
{
    public template = Template.lazy('/app/views/decontamination/order/scan/index.html');
    private scanner: Instascan.Scanner;
    private externalScanner: BBPlugin;
    public data: ScanOrderViewData;
    public isScanning = false;
    public isLoading = true;
    private scannerInitiated = false;
    public app: FortumApp;

    private availableCameras: Instascan.Camera[];

    // Bound elements
    private stopScanButton?: HTMLButtonElement;
    private form?: HTMLFormElement;
    private continueButton?: HTMLButtonElement;
    private orderIdField: HTMLInputElement;

    public beforeRender(): PromiseLike<void>
    {
        this.app.header.text = this.pgettext('decontamination.Order', 'Scan decontamination record');
        this.app.header.iconClass = AppIcons.QRCode;
        this.on(ViewDataBindEvents.DATA_CHANGED, this.onDataChanged);
        return super.beforeRender();
    }

    public afterRender(): PromiseLike<void>
    {        
        return super.afterRender().then(() =>
        {
            const onScannerButtonDown = (e: KeyboardEvent) =>
            {
                if (e.keyCode == KeyCode.F9 || e.keyCode == KeyCode.F10 || e.keyCode == KeyCode.F11 || e.keyCode == 139 || e.keyCode == 140 || e.keyCode == 141)
                {
                    this.orderIdField.focus();
                }
            };
            
            if (BBrowser.isAvailable() && BBrowser.hasPlugin('general'))
            {
                let bbGeneral = BBrowser.plugin('general');

                bbGeneral.on('onKeyEvent', onScannerButtonDown); 
                bbGeneral.call('listenKeyEvents', {handleEvents: false}); 
                console.info('Listening Bluugo Browser "onKeyEvent" event.');
            }
            else
            {
                window.addEventListener('keydown', onScannerButtonDown);
                console.info('Listening native "keydown" event.');
            }
        });
    }
    
    // Bound event handler
    public onStartScanButtonClick(e?: MouseEvent)
    {
        this.startExternalScan();
    }

    // Bound event handler
    public onContinueButtonClick(e: MouseEvent)
    {
        this.submit();
    }

    public onOrderIdFieldClick(e: MouseEvent)
    {

    }

    private handleScanResults(content)
    {
        if (this.validateOrderUUID(content))
        {
            this.data.orderId = content;
            this.submit();
        }
        else
        {
            this.app.modals.show(new MessageModal({
                message: this.pgettext('decontamination.Order','"{id}" is not a valid record id.', {id: content}),
                color: ModalColor.RED,
                iconClass: 'im im-error',
                title: this.ugettext('Error')
            }));
        }
    }

    private onDataChanged = (sender, args: DataPropertyChangedEventArgs) =>
    {
        if (args.propertyName == 'selectedCamera')
        {

        }
    };

    private validateOrderUUID(id: string)
    {
        return /^[A-Fa-f0-9-]+$/.test(id);
    }

    private startExternalScan()
    {
        if (!this.externalScanner) {
            this.externalScanner = BBrowser.plugin('zxing-lite');
            this.externalScanner.on('barcode-result', result => this.barcodeResult(result));
        }

        if (!this.externalScanner.isAvailable()) {
            new MessageModal({
                message: this.ugettext('External scanner is not enabled'),
                color: ModalColor.RED
            }).showIn(this.app);
        }

        this.externalScanner.call('scan', {types: ['QR_CODE']});
    }

    private barcodeResult(result)
    {
        if (result && result.barcode) {
            this.handleScanResults(result.barcode);
        }
    }

    public submit(data?): PromiseLike<any>
    {
        this.helpers.setFormEnabled(this.form, false);
        this.helpers.setButtonEnabled(this.continueButton, false);
        const reEnableControls = () =>
        {
            this.helpers.setFormEnabled(this.form, true);
            this.helpers.setButtonEnabled(this.continueButton, true);
        };

        let result: PromiseLike<any>;

        if (this.data.orderId == null || this.data.orderId.length == 0)
        {
            this.app.modals.show(new MessageModal({
                message: this.pgettext('Message', 'Please scan a QR code or enter the identifier manually.')
            })).then(() =>
            {
                reEnableControls();
            });
            return;
        }
        else
        {
            
            let numericId = isNaN(parseInt(this.data.orderId)) ? -1 : parseInt(this.data.orderId);

            return this.app.db.DeconOrder
                .where('uuid').equals(this.data.orderId)
                .or('ext_id').equals(this.data.orderId)
                .or('id').equals(numericId)
                // Include non-completed orders (prevents editing orders that have been completed in offline)
                .sortBy('modified_at')
                .then(orders =>
            {
                if (orders.length == 0)
                {
                    return this.app.modals.show(new MessageModal({
                        message: this.pgettext(
                            'decontamination.Order',
                            'Could not find a record with uud/ext_id/id matching "{identifier}"!',
                            {identifier: this.data.orderId}
                        ),
                        color: ModalColor.RED
                    })).then(() =>
                    {
                        reEnableControls();
                    });
                }
                else if (orders.length > 0 && orders[0].state == Order.STATE_COMPLETED) {
                    return this.app.modals.show(new MessageModal({
                        message: this.pgettext(
                            'decontamination.Order',
                            `Cannot edit record "{identifier}" - it's already completed!`,
                            {identifier: this.data.orderId}
                        ),
                        color: ModalColor.RED
                    })).then(() =>
                    {
                        reEnableControls();
                    });
                }

                location.href = `#/order/${orders[0].uuid}/detail`
            });
        }

        location.href = `#/order/${this.data.orderId}/detail`;
    }

    public unload(): PromiseLike<void>
    {
        let promise: PromiseLike<void> = Promise.resolve();

        if (this.externalScanner) {
            this.externalScanner.unplug();
        }

        return promise.then(() =>
        {
            return super.unload();
        });
    }
}

export interface ScanOrderViewData
{
    orderId?: string;
    order: {[key: string]: any};
    availableCameraOptions?: SelectOptionData[];
    selectedCamera?: SelectOptionData;
}